import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiArrowLeftBold, PiPlusBold, PiTrashBold } from 'react-icons/pi';
import { Button, PageHeader, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import type { SalesTeam, SalesTeamMemberRow } from './types';

const MANAGER_INHERIT = 'inherit';
const MANAGER_NONE = 'none';

type Translate = (key: string, replace?: Record<string, string | number>) => string;
type TeamOption = { id: number; name: string; manager_name?: string | null };
type RosterUser = { id: number; name: string; email: string; job_title: string | null };

type CascadeSyncStats = {
    deals: number;
    records_created: number;
    records_updated: number;
};

type PeriodDraft = {
    from: string;
    to: string;
    managerId: string;
    teamId: string;
};

function isoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function firstDayOfMonth(): string {
    const d = new Date();
    return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

function periodManagerSelectValue(period: SalesTeamMemberRow): string {
    if (period.skip_manager_bonus) return MANAGER_NONE;
    if (period.period_manager_id) return String(period.period_manager_id);
    return MANAGER_INHERIT;
}

function managerPeriodPayload(managerId: string): { manager_id: number | null; skip_manager_bonus: boolean } {
    if (managerId === MANAGER_NONE) return { manager_id: null, skip_manager_bonus: true };
    if (managerId === MANAGER_INHERIT || managerId === '') return { manager_id: null, skip_manager_bonus: false };
    return { manager_id: Number(managerId), skip_manager_bonus: false };
}

function draftFrom(p: SalesTeamMemberRow): PeriodDraft {
    return {
        from: p.effective_from ?? '',
        to: p.effective_to ?? '',
        managerId: periodManagerSelectValue(p),
        teamId: p.team_id ? String(p.team_id) : '',
    };
}

function toastCascadeSync(t: Translate, sync?: CascadeSyncStats) {
    if (!sync || sync.deals < 1) return;
    toast.success(t('v2.commissioning.sales_teams.sync_ok', {
        deals: sync.deals,
        created: sync.records_created,
        updated: sync.records_updated,
    }));
}

function apiError(err: unknown, fallback: string): string {
    const msg = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response?.data;
    return msg?.errors?.members?.[0] ?? msg?.message ?? fallback;
}

export default function SellerAssignmentPeriodsPage() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const { userId: userIdParam } = useParams();
    const userId = Number(userIdParam);

    const { data: periodsData, isLoading: periodsLoading } = useQuery<{ data: SalesTeamMemberRow[] }>({
        queryKey: ['commissioning', 'seller-periods', userId],
        queryFn: () => api.get('/commissioning/sales-teams/seller-periods', { params: { user_id: userId } }).then((r) => r.data),
        enabled: userId > 0,
    });

    const { data: teamsData } = useQuery<{ data: SalesTeam[] }>({
        queryKey: ['commissioning', 'sales-teams'],
        queryFn: () => api.get('/commissioning/sales-teams').then((r) => r.data),
    });

    const { data: rosterData } = useQuery<{ data: RosterUser[] }>({
        queryKey: ['commissioning', 'sales-roster', 'managers'],
        queryFn: () => api.get('/commissioning/sales-teams/roster', { params: { pool: 'managers' } }).then((r) => r.data),
    });

    const periods = periodsData?.data ?? [];
    const teams: TeamOption[] = (teamsData?.data ?? [])
        .filter((row) => row.commission_mode === 'standard')
        .map((row) => ({ id: row.id, name: row.name, manager_name: row.manager?.name ?? null }));
    const managers = useMemo(() => {
        const byId = new Map<number, RosterUser>();
        const add = (id: number, name: string, email = '', jobTitle: string | null = null) => {
            if (!id || !name) return;
            byId.set(id, { id, name, email, job_title: jobTitle });
        };
        (rosterData?.data ?? []).forEach((u) => add(u.id, u.name, u.email, u.job_title));
        (teamsData?.data ?? []).forEach((team) => {
            if (team.manager) add(team.manager.id, team.manager.name, team.manager.email ?? '');
        });
        periods.forEach((p) => {
            if (p.period_manager_id && p.period_manager_name) {
                add(p.period_manager_id, p.period_manager_name);
            }
        });
        return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [rosterData, teamsData, periods]);

    const sellerName = periods[0]?.user_name
        ?? rosterData?.data?.find((u) => u.id === userId)?.name
        ?? t('v2.commissioning.sales_teams.col_seller');

    const [drafts, setDrafts] = useState<Record<number, PeriodDraft>>({});
    const [adding, setAdding] = useState(false);
    const [newPeriod, setNewPeriod] = useState<PeriodDraft>({
        from: firstDayOfMonth(),
        to: '',
        managerId: MANAGER_NONE,
        teamId: '',
    });

    const draftOf = (p: SalesTeamMemberRow): PeriodDraft => drafts[p.id] ?? draftFrom(p);

    const setDraft = (id: number, field: keyof PeriodDraft, value: string) => {
        setDrafts((prev) => ({
            ...prev,
            [id]: { ...(prev[id] ?? draftFrom(periods.find((row) => row.id === id)!)), [field]: value },
        }));
    };

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['commissioning', 'seller-periods'] });
        qc.invalidateQueries({ queryKey: ['commissioning', 'sales-teams'] });
        qc.invalidateQueries({ queryKey: ['commissioning', 'assignment-history'] });
        qc.invalidateQueries({ queryKey: ['commissioning', 'sales-roster'] });
    };

    const patchMutation = useMutation({
        mutationFn: ({ id, draft }: { id: number; draft: PeriodDraft }) =>
            api.patch(`/commissioning/sales-teams/members/${id}`, {
                effective_from: draft.from,
                effective_to: draft.to || null,
                sales_team_id: draft.teamId ? Number(draft.teamId) : null,
                ...managerPeriodPayload(draft.managerId),
            }).then((r) => r.data),
        onSuccess: (res) => {
            toastCascadeSync(t, res.cascade_sync);
            invalidate();
        },
        onError: (err: unknown) => toast.error(apiError(err, t('v2.commissioning.sales_teams.update_error'))),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => api.delete(`/commissioning/sales-teams/members/${id}`).then((r) => r.data),
        onSuccess: (res) => {
            toastCascadeSync(t, res.cascade_sync);
            invalidate();
        },
        onError: (err: unknown) => toast.error(apiError(err, t('v2.commissioning.sales_teams.update_error'))),
    });

    const syncMutation = useMutation({
        mutationFn: (payload: { from: string; to: string | null }) =>
            api.post('/commissioning/sales-teams/sync-cascade', {
                user_id: userId,
                from: payload.from || '2000-01-01',
                to: payload.to || null,
            }).then((r) => r.data.data as CascadeSyncStats),
        onSuccess: (stats) => {
            if (stats.deals < 1) {
                toast.success(t('v2.commissioning.sales_teams.sync_empty'));
            } else {
                toastCascadeSync(t, stats);
            }
            qc.invalidateQueries({ queryKey: ['commissioning'] });
        },
        onError: () => toast.error(t('v2.commissioning.sales_teams.sync_error')),
    });

    const addMutation = useMutation({
        mutationFn: (draft: PeriodDraft) =>
            api.post('/commissioning/sales-teams/member-periods', {
                user_id: userId,
                commission_role: 'vendedor',
                effective_from: draft.from,
                effective_to: draft.to || null,
                sales_team_id: draft.teamId ? Number(draft.teamId) : null,
                ...managerPeriodPayload(draft.managerId),
            }).then((r) => r.data),
        onSuccess: (res) => {
            toastCascadeSync(t, res.cascade_sync);
            setAdding(false);
            setNewPeriod({ from: firstDayOfMonth(), to: '', managerId: MANAGER_NONE, teamId: '' });
            invalidate();
        },
        onError: (err: unknown) => toast.error(apiError(err, t('v2.commissioning.sales_teams.update_error'))),
    });

    const teamLabel = (teamId: string) => teams.find((row) => String(row.id) === teamId);

    const emptyNew = useMemo(() => (
        { from: firstDayOfMonth(), to: '', managerId: MANAGER_NONE, teamId: '' }
    ), []);

    if (!userId) {
        return <p className="text-sm text-red-600">{t('v2.commissioning.sales_teams.timeline_error')}</p>;
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={t('v2.commissioning.sales_teams.periods_page_title', { name: sellerName })}
                subtitle={t('v2.commissioning.sales_teams.periods_page_help')}
            >
                <Link
                    to="/commissioning/teams"
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:border-orange-300 hover:text-orange-600"
                >
                    <PiArrowLeftBold className="size-4" />
                    {t('v2.commissioning.sales_teams.back_to_teams')}
                </Link>
            </PageHeader>

            {periodsLoading && <LoadingState variant="table" />}

            {!periodsLoading && (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                    <div className="grid grid-cols-1 gap-2 border-b border-gray-100 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-gray-400 sm:grid-cols-[minmax(12rem,1.2fr)_minmax(12rem,1fr)_8rem_8rem_auto] dark:border-zinc-800">
                        <span>{t('v2.commissioning.sales_teams.period_team')}</span>
                        <span>{t('v2.commissioning.sales_teams.manager')}</span>
                        <span>{t('v2.commissioning.sales_teams.period_from')}</span>
                        <span>{t('v2.commissioning.sales_teams.period_to')}</span>
                        <span />
                    </div>

                    <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                        {periods.map((period) => {
                            const draft = draftOf(period);
                            const team = teamLabel(draft.teamId);
                            return (
                                <li key={period.id} className="grid grid-cols-1 items-end gap-3 px-4 py-3 sm:grid-cols-[minmax(12rem,1.2fr)_minmax(12rem,1fr)_8rem_8rem_auto]">
                                    <select
                                        className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                                        value={draft.teamId}
                                        onChange={(e) => {
                                            setDraft(period.id, 'teamId', e.target.value);
                                            if (!e.target.value && draft.managerId === MANAGER_INHERIT) {
                                                setDraft(period.id, 'managerId', MANAGER_NONE);
                                            }
                                        }}
                                    >
                                        <option value="">{t('v2.commissioning.sales_teams.period_no_team')}</option>
                                        {teams.map((row) => (
                                            <option key={row.id} value={row.id}>{row.name}</option>
                                        ))}
                                    </select>
                                    <select
                                        className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                                        value={draft.managerId}
                                        onChange={(e) => setDraft(period.id, 'managerId', e.target.value)}
                                    >
                                        {draft.teamId ? (
                                            <option value={MANAGER_INHERIT}>
                                                {t('v2.commissioning.sales_teams.manager_inherit', {
                                                    name: team?.manager_name ?? team?.name ?? '—',
                                                })}
                                            </option>
                                        ) : null}
                                        <option value={MANAGER_NONE}>{t('v2.commissioning.sales_teams.no_manager')}</option>
                                        {managers.map((m) => (
                                            <option key={m.id} value={m.id}>{m.name}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="date"
                                        className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                                        value={draft.from}
                                        onChange={(e) => setDraft(period.id, 'from', e.target.value)}
                                    />
                                    <input
                                        type="date"
                                        className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                                        value={draft.to}
                                        onChange={(e) => setDraft(period.id, 'to', e.target.value)}
                                        placeholder={t('v2.commissioning.sales_teams.period_open')}
                                    />
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                            onClick={() => patchMutation.mutate({ id: period.id, draft })}
                                            disabled={patchMutation.isPending || !draft.from}
                                            className="text-xs"
                                        >
                                            {t('v2.commissioning.sales_teams.period_save')}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => syncMutation.mutate({ from: draft.from, to: draft.to || null })}
                                            disabled={syncMutation.isPending || !draft.from}
                                            className="text-xs"
                                        >
                                            {t('v2.commissioning.sales_teams.sync_period')}
                                        </Button>
                                        <button
                                            type="button"
                                            className="p-1.5 text-gray-400 hover:text-red-600"
                                            onClick={() => {
                                                if (confirm(t('v2.commissioning.sales_teams.period_delete_confirm'))) {
                                                    deleteMutation.mutate(period.id);
                                                }
                                            }}
                                        >
                                            <PiTrashBold className="size-4" />
                                        </button>
                                    </div>
                                </li>
                            );
                        })}

                        {adding && (
                            <li className="grid grid-cols-1 items-end gap-3 bg-orange-50/40 px-4 py-3 sm:grid-cols-[minmax(12rem,1.2fr)_minmax(12rem,1fr)_8rem_8rem_auto] dark:bg-orange-950/10">
                                <select
                                    className="w-full rounded-lg border border-orange-300 bg-white px-2 py-1.5 text-sm dark:border-orange-700 dark:bg-zinc-900"
                                    value={newPeriod.teamId}
                                    onChange={(e) => setNewPeriod((p) => ({
                                        ...p,
                                        teamId: e.target.value,
                                        managerId: e.target.value ? p.managerId : MANAGER_NONE,
                                    }))}
                                >
                                    <option value="">{t('v2.commissioning.sales_teams.period_no_team')}</option>
                                    {teams.map((row) => (
                                        <option key={row.id} value={row.id}>{row.name}</option>
                                    ))}
                                </select>
                                <select
                                    className="w-full rounded-lg border border-orange-300 bg-white px-2 py-1.5 text-sm dark:border-orange-700 dark:bg-zinc-900"
                                    value={newPeriod.managerId}
                                    onChange={(e) => setNewPeriod((p) => ({ ...p, managerId: e.target.value }))}
                                >
                                    {newPeriod.teamId ? (
                                        <option value={MANAGER_INHERIT}>
                                            {t('v2.commissioning.sales_teams.manager_inherit', {
                                                name: teamLabel(newPeriod.teamId)?.manager_name ?? teamLabel(newPeriod.teamId)?.name ?? '—',
                                            })}
                                        </option>
                                    ) : null}
                                    <option value={MANAGER_NONE}>{t('v2.commissioning.sales_teams.no_manager')}</option>
                                    {managers.map((m) => (
                                        <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                </select>
                                <input
                                    type="date"
                                    className="w-full rounded-lg border border-orange-300 bg-white px-2 py-1.5 text-sm dark:border-orange-700 dark:bg-zinc-900"
                                    value={newPeriod.from}
                                    onChange={(e) => setNewPeriod((p) => ({ ...p, from: e.target.value }))}
                                />
                                <input
                                    type="date"
                                    className="w-full rounded-lg border border-orange-300 bg-white px-2 py-1.5 text-sm dark:border-orange-700 dark:bg-zinc-900"
                                    value={newPeriod.to}
                                    onChange={(e) => setNewPeriod((p) => ({ ...p, to: e.target.value }))}
                                />
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={() => addMutation.mutate(newPeriod)}
                                        disabled={addMutation.isPending || !newPeriod.from}
                                        className="text-xs"
                                    >
                                        {t('v2.commissioning.sales_teams.period_save')}
                                    </Button>
                                    <button
                                        type="button"
                                        className="text-xs text-gray-400 hover:text-gray-600"
                                        onClick={() => { setAdding(false); setNewPeriod(emptyNew); }}
                                    >
                                        {t('v2.commissioning.sales_teams.cancel')}
                                    </button>
                                </div>
                            </li>
                        )}
                    </ul>

                    {!adding && (
                        <div className="border-t border-gray-100 px-4 py-3 dark:border-zinc-800">
                            <button
                                type="button"
                                onClick={() => setAdding(true)}
                                className="inline-flex items-center gap-1 text-sm font-medium text-orange-600 hover:text-orange-700"
                            >
                                <PiPlusBold className="size-4" />
                                {t('v2.commissioning.sales_teams.period_add')}
                            </button>
                        </div>
                    )}

                    {periods.length === 0 && !adding && (
                        <p className="px-4 pb-4 text-sm text-gray-400">{t('v2.commissioning.sales_teams.timeline_empty')}</p>
                    )}
                </div>
            )}

            <p className="text-xs text-gray-500">{t('v2.commissioning.sales_teams.periods_page_hint')}</p>
        </div>
    );
}
